import { filterNonNull, guardArray } from "@msuite/utils/array";
import { type IconType, icon } from "../icons";
import { localized } from "../localization";
import type { MenuItems } from "./menu";
import type { MenuItemType } from "./menu-item";

/** Props Interface */
interface StandardMenuItemsOptions {
	onOpen?: () => void;
	onEdit?: () => void;
	onDelete?: () => void;
	onOpenOptions?: Partial<MenuItemType>;
	onEditOptions?: Partial<MenuItemType>;
	onDeleteOptions?: Partial<MenuItemType>;
	items?: MenuItems;
}

export function createStandardMenuItems({
	onOpen,
	onEdit,
	onDelete,
	onOpenOptions = {},
	onEditOptions = {},
	onDeleteOptions = {},
	items,
}: StandardMenuItemsOptions) {
	const upperItems: MenuItems = [
		onOpen
			? {
					label: localized("general.open"),
					icon: icon("Info") as IconType,
					onClick: onOpen,
					...onOpenOptions,
				}
			: null,
		onEdit
			? {
					label: localized("general.edit"),
					icon: icon("Edit") as IconType,
					onClick: onEdit,
					...onEditOptions,
				}
			: null,
	];

	const lowerItems: MenuItems = [
		onDelete
			? {
					label: localized("general.delete"),
					icon: icon("Trash2") as IconType,
					onClick: onDelete,
					isDanger: true,
					...onDeleteOptions,
				}
			: null,
	];

	const _items = filterNonNull(upperItems)
		.concat(filterNonNull(guardArray(items)))
		.concat(filterNonNull(lowerItems));

	return () => _items;
}

/**
 * Drop the separators that separate nothing: a leading one, a trailing one, and any run of them
 * with no entry in between.
 *
 * A menu whose entries are each decided by their own rule cannot know, at the point it writes a
 * separator, whether anything will still stand on both sides of it. Building the list with every
 * separator in place and passing it through here keeps that in one spot, and keeps a caller denied
 * the writes from opening a menu with a stray line in it.
 *
 * @param items - The menu items, separators included and the denied entries already null.
 * @returns The same list without the separators that have nothing on one side.
 */
export function withoutDanglingDividers(items: MenuItems): MenuItems {
	const kept: MenuItems = [];
	for (const item of filterNonNull(guardArray(items))) {
		if (item !== "divider") {
			kept.push(item);
			continue;
		}
		if (kept.length && kept[kept.length - 1] !== "divider") kept.push(item);
	}
	if (kept[kept.length - 1] === "divider") kept.pop();
	return kept;
}
